import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../_services/report-template-design.service';
import { FONT_FAMILY, BORDER_STYLE, TABLE_RULES } from '../app.constants';
import { ReportElement } from './report-element';
import { ListItem, TemplateMeasurementPoint, ElementLocationInSection } from './report-template';
import { ReportUnits } from './report-units';
import { ReportPaperSize } from './report-paper-size';
import { ReportPageMargin } from './report-page-margin';
import { ReportCommonSection } from './report-common-section';
import { ElementType } from './element-type';

export class ReportMeasurementPoint extends ReportElement {
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public fontSize: string;
  public fontFamily: string;
  public tableZIndex: number;
  public columnLabel: string;
  public columnMapping: string;
  public columns: ListItem[];
  public tableBorderWidth: number;
  public tableBorderStyle: string;
  public tableBorderColor: string;
  public tableMargin: number;
  public tableRules: string;
  public cellMargin: number;
  public isStriped: boolean;
  public dynamicDataField: any;
  public tableWidth: string;
  public titleText: string;
  public titleFontSize: string;
  public titleAlign: string;
  public titleBackgroundColor: string;

  private units: ReportUnits;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  private baseDef: TemplateMeasurementPoint;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';
  private readonly metaZIndex = 'zIndex';
  private readonly metaColumns = 'columns';
  private readonly metaTableBorderWidth = 'tableBorderWidth';
  private readonly metaTableBorderStyle = 'tableBorderStyle';
  private readonly metaTableBorderColor = 'tableBorderColor';
  private readonly metaTableMargin = 'tableMargin';
  private readonly metaTableRules = 'tableRules';
  private readonly metaCellMargin = 'cellMargin';
  private readonly metaIsStriped = 'isStriped';
  private readonly metaTableWidth = 'tableWidth';
  private readonly metaTitleText = 'titleText';
  private readonly metaTitleFontSize = 'titleFontSize';
  private readonly metaTitleAlign = 'titleAlign';
  private readonly metaTitleBackgroundColor = 'titleBackgroundColor';

  public constructor(
    type: ElementType,
    index: number,
    units: ReportUnits,
    header: ReportCommonSection,
    footer: ReportCommonSection,
    paperSize: ReportPaperSize,
    pageMargin: ReportPageMargin,
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateTable?: TemplateMeasurementPoint
  ) {
    super(type, index, !(templateTable == null), translate, langService, templateTable ? templateTable.id : null);

    this.units = units;
    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateTable) {
      this.baseDef = templateTable;
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: paperSize.getWidthNum(),
        tableWidth: paperSize.getWidthNum(),
        height: paperSize.getHeightNum(),
        fontSize: 4,
        fontFamily: '',
        titleText: this.translate.instant('objects.measurementResults'),
        titleAlign: 'Left',
        titleBackgroundColor: 'transparent',
        titleFontSize: 6,
        zIndex: 0,
        columns: [
          { label: this.translate.instant('objects.Header1'), dataField: '' },
          { label: this.translate.instant('objects.Header2'), dataField: '' },
          { label: this.translate.instant('objects.Header3'), dataField: '' },
        ],
        tableBorderWidth: 0.25,
        tableBorderStyle: 'solid',
        tableBorderColor: '#ccc',
        tableMargin: 2,
        tableRules: 'rows',
        cellMargin: 2,
        isStriped: true,
        dynamicDataField: '',
      } as TemplateMeasurementPoint;
    }

    this.top = this.units.concatenate('sizeUnit', this.baseDef[this.metaTop]);
    this.left = this.units.concatenate('sizeUnit', this.baseDef[this.metaLeft]);

    this.width = this.units.concatenate('sizeUnit', this.baseDef[this.metaWidth]);
    this.tableWidth = this.units.concatenate('sizeUnit', this.baseDef[this.metaTableWidth] - 20);
    this.height = this.units.concatenate('sizeUnit', this.baseDef[this.metaHeight]);

    this.fontSize = this.units.concatenate('sizeUnit', this.baseDef[this.metaFontSize]);
    this.fontFamily = this.baseDef[this.metaFontFamily];
    this.columns = this.baseDef[this.metaColumns];
    this.tableBorderWidth = this.baseDef[this.metaTableBorderWidth];
    this.tableBorderStyle = this.baseDef[this.metaTableBorderStyle];
    this.tableBorderColor = this.baseDef[this.metaTableBorderColor];
    this.tableMargin = this.baseDef[this.metaTableMargin];
    this.tableRules = this.baseDef[this.metaTableRules];
    this.cellMargin = this.baseDef[this.metaCellMargin];
    this.isStriped = this.baseDef[this.metaIsStriped];

    this.titleText = this.baseDef[this.metaTitleText];
    this.titleFontSize = this.units.concatenate('sizeUnit', this.baseDef[this.metaTitleFontSize]);
    this.titleBackgroundColor = this.baseDef[this.metaTitleBackgroundColor];
    this.titleAlign = this.baseDef[this.metaTitleAlign];

    this.tableZIndex = this.baseDef[this.metaZIndex];
    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop],
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft],
    });

    const width = this.baseDef[this.metaWidth] - this.pageMargin.getRightNum() - this.pageMargin.getLeftNum();
    const height =
      this.baseDef[this.metaHeight] -
      this.pageMargin.getTopNum() -
      this.pageMargin.getBottomNum() -
      this.header.getHeightNum() -
      this.footer.getHeightNum();
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: width,
      oldValue: this.baseDef[this.metaWidth],
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaTableWidth,
      value: width - 2 * this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTableWidth],
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: height,
      oldValue: this.baseDef[this.metaHeight],
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft],
    });
  }

  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight],
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }

  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }

  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }

  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }

  public getTemplateJSON(): TemplateMeasurementPoint {
    return this.baseDef;
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaTitleAlign,
        parentName: null,
        value: this.titleAlign,
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
      },
      {
        name: this.metaTableMargin,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
      },
      {
        name: this.metaCellMargin,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
      },
      {
        name: this.metaTableBorderWidth,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
      },
      {
        name: this.metaTableBorderColor,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
      },
      {
        name: this.metaTableBorderStyle,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
      },
      {
        name: this.metaTitleBackgroundColor,
        parentName: null,
        value: this.titleBackgroundColor,
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Background',
      },
      {
        name: this.metaFontFamily,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_FAMILY,
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
      },
      {
        name: this.metaFontSize,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
      },
      {
        name: this.metaTitleFontSize,
        parentName: null,
        value: this.titleFontSize,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
      },
      {
        name: this.metaTitleText,
        parentName: null,
        value: this.titleText,
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
      },
      {
        name: this.metaIsStriped,
        parentName: null,
        value: this.baseDef.isStriped,
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
      },
      {
        name: this.metaTableRules,
        parentName: null,
        value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: TABLE_RULES,
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
      },
      {
        name: this.metaColumns,
        parentName: null,
        value: JSON.stringify(this.baseDef.columns),
        isFieldType: (type: FieldType): boolean => type === 'List',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
      },
    ];
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.fontSize = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontFamily = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaIsStriped:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.isStriped = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.tableZIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;

      case this.metaTableBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.tableBorderWidth = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTableBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.tableBorderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTableBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.tableBorderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTableRules:
        this.baseDef[metadata.name] = metadata.value as string;
        this.tableRules = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTableMargin:
        this.baseDef[metadata.name] = metadata.value as number;
        this.tableMargin = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaCellMargin:
        this.baseDef[metadata.name] = metadata.value as number;
        this.cellMargin = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaColumns:
        this.baseDef[metadata.name] = metadata.value as ListItem[];
        this.columns = metadata.value as ListItem[];
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleText:
        this.baseDef[metadata.name] = metadata.value as string;
        this.titleText = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.titleFontSize = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleBackgroundColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.titleBackgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleAlign:
        this.baseDef[metadata.name] = metadata.value as string;
        this.titleAlign = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    // empty
  }

  private getHeightNum(): number {
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    rtn = rtn - this.header.getHeightNum() - this.footer.getHeightNum() - this.baseDef.y * 2;
    rtn = Math.floor(rtn);
    return rtn;
  }

  private getHeight(): string {
    return [this.getHeightNum(), 'mm'].join('');
  }

  private getWidthNum(): number {
    let rtn = this.paperSize.getWidthNum();
    if (this.type === ElementType.Summary) {
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - this.baseDef.x * 2;
    } else {
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - this.baseDef.x * 2 - 5;
    }
    rtn = Math.floor(rtn);
    return rtn;
  }

  private getWidth(): string {
    return [this.getWidthNum(), 'mm'].join('');
  }
}
